/**
 * ClientIdeaDetailDrawer
 *
 * Client-portal detail surface for a single idea. Renders the shared
 * ``IdeaDetailBody`` read-only (DD-013 order: source embed + sources,
 * then every non-empty text section) so the client votes with the same
 * information the admin sees — tasks stay admin-only. The drawer adds
 * its own review chrome: 1‑5 star rating and an optional comment.
 */

import React, { useEffect, useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { IdeaDetailBody } from "@/domains/Ideas/components/IdeaDetailBody";
import { IdeaStarRating } from "@/domains/Ideas/components/IdeaStarRating";
import { CheckIcon } from "@/components/icons";
import { useLocale } from "@/hooks/useLocale";
import type { ClientIdea } from "../services/clientIdeasService";

interface ClientIdeaDetailDrawerProps {
  idea: ClientIdea | null;
  isOpen: boolean;
  onClose: () => void;
  rating: number | null;
  comment: string;
  onRate: (rating: number | null) => void;
  onCommentChange: (comment: string) => void;
}

const CONTENT_TYPE_IT: Record<string, string> = {
  POST: "Post",
  REEL: "Reel",
  CAROUSEL: "Carousel",
  STORY: "Story",
};

export const ClientIdeaDetailDrawer: React.FC<ClientIdeaDetailDrawerProps> = ({
  idea,
  isOpen,
  onClose,
  rating,
  comment,
  onRate,
  onCommentChange,
}) => {
  const { t } = useLocale();

  // Local draft so typing is responsive; auto-saves to the parent on
  // blur (and when the drawer closes). Re-syncs whenever the active
  // idea changes — the parent owns the truth.
  const [draftComment, setDraftComment] = useState(comment);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setDraftComment(comment);
  }, [comment, idea?.id]);

  const flushComment = () => {
    if (draftComment.trim() === comment.trim()) return;
    onCommentChange(draftComment);
    setJustSaved(true);
    const t = setTimeout(() => setJustSaved(false), 1600);
    return () => clearTimeout(t);
  };

  // Render the chrome even when idea is null so the drawer can animate
  // closed cleanly. The body is gated below.
  const header = idea ? (
    <div>
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.12em]">
        {idea.contentType ? CONTENT_TYPE_IT[idea.contentType] || idea.contentType : "Idea"}
      </p>
      <h2 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 leading-tight">
        {idea.title}
      </h2>
    </div>
  ) : null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={header}
      bodyPadding
      a11yTitle="Dettaglio idea"
      a11yDescription="Visualizza i dettagli dell'idea e assegna una valutazione."
    >
      {idea && (
        <div className="space-y-6">
          <IdeaDetailBody
            resources={idea.resources || []}
            body={idea.body || {}}
            sourcesLabel={t("idea.sources")}
            sectionLabels={{
              concept: t("idea.section.concept"),
              objective: t("idea.section.objective"),
              caption: t("idea.section.caption"),
              voiceover_script: t("idea.section.voiceover_script"),
              storyboard: t("idea.section.storyboard"),
            }}
          />

          <section className="border-t border-gray-100 pt-5 space-y-5">
            {/* Star rating */}
            <div>
              <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.12em] mb-3">
                {rating !== null ? "Il tuo voto" : "Vota questa idea"}
              </h3>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-gray-600 leading-snug max-w-[60%]">
                  {rating !== null
                    ? "Tocca una stella per cambiare il voto, o tocca quella attuale per rimuoverlo."
                    : "Quanto ti convince? 1 stella = poco, 5 stelle = perfetta."}
                </p>
                <IdeaStarRating
                  value={rating}
                  onChange={onRate}
                  size="lg"
                  ariaLabel={`Vota: ${idea.title}`}
                />
              </div>
            </div>

            {/* Comment — optional free-text feedback. Auto-saves on blur. */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.12em]">
                  Commento (facoltativo)
                </h3>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 transition-opacity duration-300 ${
                    justSaved ? "opacity-100" : "opacity-0"
                  }`}
                  aria-live="polite"
                >
                  <CheckIcon className="w-3 h-3" />
                  Salvato
                </span>
              </div>
              <textarea
                value={draftComment}
                onChange={(e) => setDraftComment(e.target.value)}
                onBlur={flushComment}
                placeholder="Scrivi cosa ti convince o cosa cambieresti…"
                rows={3}
                className="w-full px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 resize-y leading-relaxed"
              />
            </div>
          </section>
        </div>
      )}
    </Drawer>
  );
};
